/* eslint-disable camelcase */
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Button, Stack, Popover, MenuItem, Typography, Grid, Box, alpha } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import { useNavigate } from 'react-router-dom';
import Iconify from '../components/iconify';
import { UserListToolbar } from '../sections/@dashboard/user';
import { LightTooltip } from '../components/custom-components/custom-tooltip';
import StatsCards from '../components/common/statsCard';
import { useClientOperations } from '../hooks/useClientOperations';
import ClientsTable from '../components/clients/ClientsTable';
import ClientModals from '../components/clients/ClientModals';
import ClientAssignmentDialog from '../components/clients/ClientAssignmentDialog';
import UnassignConfirmationDialog from '../components/clients/UnassignConfirmationDialog';

export default function ClientsPage() {
  const navigate = useNavigate();

  const {
    pageLoader,
    loading,
    confirmLoader,
    credentialsLoader,
    agreementsLoader,
    clients,
    clientAccessCredentials,
    clientContracts,
    availableEmployees,
    stats,
    pagination,
    clientAccessCredentialsModalOpen,
    clientContractsModalOpen,
    clientLockAccountModalOpen,
    clientResetPasswordModalOpen,
    clientEditAccountModalOpen,
    assignmentDialog,
    unassignConfirmDialog,
    selectedClient,
    selectedClientAccountStatus,
    currentAssignmentClient,
    selectedEmployee,
    clientToUnassign,
    handleLoadClients,
    handleClientCredentialsModalClose,
    handleClientContractsModalClose,
    handleClientLockAccountModalClose,
    handleClientEditAccountModalClose,
    handleClientResetPasswordModalClose,
    handleClientLockAccount,
    handleContractDownload,
    handleClientActions,
    handleAssignmentDialogOpen,
    handleAssignmentDialogClose,
    handleAssignClient,
    handleUnassignClient,
    handleUnassignConfirm,
    handleUnassignCancel,
    setSelectedEmployee,
    handleEditClientSubmit,
    handleResetPasswordSubmit,
  } = useClientOperations();

  const [open, setOpen] = useState(null);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const isNotFound = !clients.length && !!searchQuery;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = clients?.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = async (event, newPage) => {
    const pageNum = newPage + 1;
    await handleLoadClients(pageNum, searchQuery);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    await handleLoadClients(1, searchQuery, newRowsPerPage);
  };

  const handleFilterByName = (event) => {
    const newSearch = event.target.value;
    setFilterName(newSearch);
    setSearchQuery(newSearch);
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(async () => {
      await handleLoadClients(1, newSearch);
    }, 500);
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Clients</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Client Management
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Manage your client directory, track account statuses, and handle service assignments.
              </Typography>
            </Box>
            <LightTooltip title="Add a new client to the directory.">
              <Button
                variant="contained"
                onClick={() => navigate('new', { replace: true })}
                startIcon={<Iconify icon="eva:plus-fill" />}
                sx={{
                  backgroundColor: '#0A9EF2',
                  boxShadow: () => alpha('#0A9EF2', 0.24),
                  '&:hover': {
                    backgroundColor: '#0886cc',
                    boxShadow: 'none',
                  },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                New Client
              </Button>
            </LightTooltip>
          </Stack>

          <StatsCards
            stats={[
              {
                title: 'Total Clients',
                value: stats.total_clients,
                icon: <PeopleIcon color="primary" />,
              },
              {
                title: 'Active Clients',
                value: stats.active_clients,
                icon: <EmojiPeopleIcon color="primary" />,
              },
              {
                title: 'Locked Clients',
                value: stats.locked_clients,
                icon: <PersonOffIcon color="primary" />,
              },
              {
                title: 'Assigned Clients',
                value: stats.assigned_clients,
                icon: <AssignmentIndIcon color="success" />,
              },
              {
                title: 'Unassigned Clients',
                value: stats.unassigned_clients,
                icon: <PersonAddDisabledIcon color="warning" />,
              },
            ]}
          />

          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />
          <ClientsTable
            clients={clients}
            pageLoader={pageLoader}
            pagination={pagination}
            rowsPerPage={rowsPerPage}
            order={order}
            orderBy={orderBy}
            selected={selected}
            filterName={filterName}
            isNotFound={isNotFound}
            onRequestSort={handleRequestSort}
            onSelectAllClick={handleSelectAllClick}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onAssignmentDialogOpen={handleAssignmentDialogOpen}
            onUnassignClient={handleUnassignClient}
            onClientActions={handleClientActions}
          />

          <Popover
            open={Boolean(open)}
            anchorEl={open}
            onClose={() => setOpen(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                p: 1,
                width: 140,
                '& .MuiMenuItem-root': {
                  px: 1,
                  typography: 'body2',
                  borderRadius: 0.75,
                },
              },
            }}
          >
            <MenuItem disabled>
              <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
              Edit
            </MenuItem>

            <MenuItem sx={{ color: 'error.main' }} disabled>
              <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
              Delete
            </MenuItem>
          </Popover>

          <ClientModals
            clientLockAccountModalOpen={clientLockAccountModalOpen}
            clientEditAccountModalOpen={clientEditAccountModalOpen}
            clientResetPasswordModalOpen={clientResetPasswordModalOpen}
            clientAccessCredentialsModalOpen={clientAccessCredentialsModalOpen}
            clientContractsModalOpen={clientContractsModalOpen}
            confirmLoader={confirmLoader}
            loading={loading}
            credentialsLoader={credentialsLoader}
            agreementsLoader={agreementsLoader}
            selectedClient={selectedClient}
            selectedClientAccountStatus={selectedClientAccountStatus}
            clientAccessCredentials={clientAccessCredentials}
            clientContracts={clientContracts}
            onClientLockAccountModalClose={handleClientLockAccountModalClose}
            onClientEditAccountModalClose={handleClientEditAccountModalClose}
            onClientResetPasswordModalClose={handleClientResetPasswordModalClose}
            onClientCredentialsModalClose={handleClientCredentialsModalClose}
            onClientContractsModalClose={handleClientContractsModalClose}
            onClientLockAccount={handleClientLockAccount}
            onContractDownload={handleContractDownload}
            onEditClientSubmit={handleEditClientSubmit}
            onResetPasswordSubmit={handleResetPasswordSubmit}
          />

          <ClientAssignmentDialog
            assignmentDialog={assignmentDialog}
            currentAssignmentClient={currentAssignmentClient}
            availableEmployees={availableEmployees}
            selectedEmployee={selectedEmployee}
            onAssignmentDialogClose={handleAssignmentDialogClose}
            onAssignClient={handleAssignClient}
            onSelectedEmployeeChange={setSelectedEmployee}
          />

          <UnassignConfirmationDialog
            unassignConfirmDialog={unassignConfirmDialog}
            clientToUnassign={clientToUnassign}
            onUnassignCancel={handleUnassignCancel}
            onUnassignConfirm={handleUnassignConfirm}
          />
        </Grid>
      </Grid>
    </>
  );
}
